import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
// Retained for the one-shot stale-token purge on boot (see AuthContext).
export const LEGACY_TOKEN_KEY = "auth_token";
// Compat alias — a handful of older imports still reference TOKEN_KEY.
// After V1.3.1 the value is unused; V1.4 can drop the export entirely.
export const TOKEN_KEY = LEGACY_TOKEN_KEY;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // Batch V1.3 — cookie transport.
});

// Single-flight refresh coalescer.
let _refreshInFlight = null;

async function _refresh() {
  if (!_refreshInFlight) {
    _refreshInFlight = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => {
        _refreshInFlight = null;
      });
  }
  return _refreshInFlight;
}

// Callback invoked when we've exhausted refresh — the AuthContext installs
// its own handler to bounce the user to /login.
let _onAuthExpired = () => {};
export function setOnAuthExpired(cb) {
  _onAuthExpired = typeof cb === "function" ? cb : () => {};
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    // Only try to refresh on 401s, and only once per request. Don't try to
    // refresh the refresh endpoint itself or the login endpoint.
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !(original.url || "").includes("/auth/refresh") &&
      !(original.url || "").includes("/auth/login")
    ) {
      original._retried = true;
      try {
        await _refresh();
        return api.request(original);
      } catch (e) {
        _onAuthExpired();
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(", ");
  }
  return JSON.stringify(detail);
}
